// app/features/user-profile/components/ClientDataSectionComponent.tsx
'use client';

import { formatDistanceToNow } from 'date-fns';
import { ChevronDown, ChevronRight, Database, ExternalLink, FileText, Heart, Link as LinkIcon, Stethoscope, User } from 'lucide-react';
import React, { useState } from 'react';

import { HTFBadge } from '@/app/shared/components/feedback/HTFBadge';

// Custom field labels for better display
const FIELD_LABELS: Record<string, string> = {
  'pdf_url': 'Protocol PDF URL',
  'protocol_id': 'Protocol ID',
  'anamnesis_type': 'Anamnesis Type',
};

// Format field names: snake_case -> Title Case (with custom overrides)
const formatFieldName = (key: string): string => {
  // Check for custom label first
  if (FIELD_LABELS[key.toLowerCase()]) {
    return FIELD_LABELS[key.toLowerCase()];
  }
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Fields to hide
const HIDDEN_FIELDS = ['created_at', 'updated_at', 'whatsapp_number', 'patient_id', 'id', 'platform_user_id', 'conversation_state_id'];

const shouldHideField = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return HIDDEN_FIELDS.some(hidden => lowerKey === hidden || lowerKey.endsWith('_id'));
};

// Check if value is primitive
const isSimpleValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
};

// Render a value with proper formatting
const renderValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 italic">—</span>;
  }
  
  if (typeof value === 'boolean') {
    return (
      <HTFBadge variant={value ? 'success' : 'secondary'} size="md" rounded="full">
        {value ? 'Yes' : 'No'}
      </HTFBadge>
    );
  }
  
  if (typeof value === 'string') {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return (
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-teal-dark hover:text-teal font-semibold inline-flex items-center gap-1.5 underline"
        >
          <ExternalLink className="w-4 h-4" />
          View
        </a>
      );
    }
    
    if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
      try {
        return <span className="font-semibold text-gray-900">{new Date(value).toLocaleDateString()}</span>;
      } catch {
        // fallthrough
      }
    }
    
    return <span className="font-medium text-gray-900">{value}</span>;
  }
  
  return <span className="font-medium text-gray-900">{String(value)}</span>;
};

// Simple data row with alternating background
function DataRow({ label, value, index }: { label: string; value: React.ReactNode; index?: number }) {
  const bgClass = index !== undefined && index % 2 === 0 ? 'bg-surface' : '';
  
  return (
    <div className={`flex justify-between items-start py-3 px-4 rounded-lg ${bgClass}`}>
      <span className="text-gray-800 font-medium">{label}</span>
      <div className="text-right max-w-[65%]">
        {value}
      </div>
    </div>
  );
}

// Section heading (like markdown H2)
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-lg font-bold text-gray-900 mt-6 mb-3 first:mt-0">
      {children}
    </h4>
  );
}

// Sub-section heading (like markdown H3)
function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h5 className="text-base font-semibold text-gray-800 mt-4 mb-2">
      {children}
    </h5>
  );
}

// Render a scientific reference card
function ReferenceCard({ ref: reference, index }: { ref: Record<string, any>; index: number }) {
  return (
    <div className="bg-surface-light rounded-xl p-5 flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal/15 flex items-center justify-center">
        <LinkIcon className="w-5 h-5 text-teal-dark" />
      </div>
      <div className="flex-1 min-w-0">
        {reference.title && (
          <p className="font-bold text-gray-900 leading-snug mb-2">{reference.title}</p>
        )}
        {(reference.key_finding || reference.key_protocol_finding) && (
          <p className="text-gray-700 text-sm leading-relaxed mb-3">
            {reference.key_finding || reference.key_protocol_finding}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-4">
          {reference.pmid && (
            <span className="text-sm text-gray-600 font-medium">PMID: {reference.pmid}</span>
          )}
          {reference.url && (
            <a 
              href={reference.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-teal-dark hover:text-teal font-semibold inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View Article
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// Render any nested object FLAT - no expansion, just headings and rows
function RenderDataFlat({ data, parentKey = '' }: { data: Record<string, any>; parentKey?: string }) {
  const entries = Object.entries(data).filter(([key]) => !shouldHideField(key));
  let rowIndex = 0;
  
  const elements: React.ReactNode[] = [];
  
  for (const [key, value] of entries) {
    // Check if it's scientific references
    if (key.toLowerCase().includes('reference') && Array.isArray(value) && value.length > 0 && value[0].title) {
      elements.push(
        <div key={key}>
          <SectionHeading>{formatFieldName(key)}</SectionHeading>
          <div className="space-y-3">
            {value.map((ref: Record<string, any>, i: number) => (
              <ReferenceCard key={i} ref={ref} index={i} />
            ))}
          </div>
        </div>
      );
      continue;
    }
    
    // Handle nested objects - flatten with sub-heading
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      elements.push(
        <div key={key}>
          <SectionHeading>{formatFieldName(key)}</SectionHeading>
          <RenderDataFlat data={value} parentKey={key} />
        </div>
      );
      continue;
    }
    
    // Handle arrays of objects
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      elements.push(
        <div key={key}>
          <SectionHeading>{formatFieldName(key)}</SectionHeading>
          <div className="space-y-4">
            {value.map((item: Record<string, any>, i: number) => (
              <div key={i} className="bg-surface rounded-xl p-4">
                <SubHeading>Item {i + 1}</SubHeading>
                <RenderDataFlat data={item} parentKey={`${key}_${i}`} />
              </div>
            ))}
          </div>
        </div>
      );
      continue;
    }
    
    // Handle simple arrays (tags, etc)
    if (Array.isArray(value)) {
      elements.push(
        <DataRow 
          key={key}
          label={formatFieldName(key)}
          index={rowIndex++}
          value={
            value.length === 0 ? (
              <span className="text-gray-400 italic">—</span>
            ) : (
              <div className="flex flex-wrap gap-1.5 justify-end">
                {value.map((item, i) => (
                  <HTFBadge key={i} variant="secondary" size="sm" rounded="full">
                    {String(item)}
                  </HTFBadge>
                ))}
              </div>
            )
          }
        />
      );
      continue;
    }
    
    // Simple value
    elements.push(
      <DataRow 
        key={key}
        label={formatFieldName(key)}
        index={rowIndex++}
        value={renderValue(value)}
      />
    );
  }
  
  return <div className="space-y-1">{elements}</div>;
}

// Main expandable card for each section
function SectionCard({ 
  title, 
  icon: Icon, 
  children,
  subtitle,
  defaultOpen = false
}: { 
  title: string; 
  icon: React.ElementType;
  children: React.ReactNode;
  subtitle?: string;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-beige-light rounded-xl overflow-hidden">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between hover:bg-beige/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-teal/15 flex items-center justify-center">
            <Icon className="w-6 h-6 text-teal-dark" />
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            {subtitle && <p className="text-sm text-gray-600 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {isOpen ? (
          <ChevronDown className="w-6 h-6 text-gray-500" />
        ) : (
          <ChevronRight className="w-6 h-6 text-gray-500" />
        )}
      </button>
      
      {isOpen && (
        <div className="px-6 pb-6 border-t border-beige">
          <div className="pt-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

interface ClientDataSectionComponentProps {
  clientSpecificData: Record<string, any>;
  collectedData: Record<string, any>;
  lastUpdated: string | null;
  clientData?: Record<string, any>;
}

export function ClientDataSectionComponent({ 
  clientSpecificData, 
  collectedData,
  lastUpdated,
  clientData
}: ClientDataSectionComponentProps) {
  // Filter hidden fields
  const clientEntries = Object.entries(clientSpecificData || {}).filter(([key]) => !shouldHideField(key));
  const collectedEntries = Object.entries(collectedData || {}).filter(([key]) => !shouldHideField(key));
  const clientDbEntries = Object.entries(clientData || {}).filter(([key]) => !key.startsWith('_') && !shouldHideField(key));
  
  const hasAnyData = clientEntries.length > 0 || collectedEntries.length > 0 || clientDbEntries.length > 0;
  
  // Extract known sections
  const patientInfo = clientData?.patient_info;
  const anamnesisData = clientDbEntries.find(([key]) => key === 'anamnesis')?.[1];
  const protocolData = clientDbEntries.find(([key]) => key === 'protocol')?.[1];
  
  if (!hasAnyData) {
    return (
      <div className="bg-beige-light rounded-xl p-12 text-center">
        <Database className="w-16 h-16 text-gray-300 mx-auto mb-6" />
        <h3 className="text-2xl font-bold text-gray-900 mb-3">No Client Data</h3>
        <p className="text-lg text-gray-700">
          No client-specific data has been collected for this user yet.
        </p>
        <p className="text-base text-gray-600 mt-3">
          Data will appear here as the user interacts with the AI agent.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 1. DEMOGRAPHICS */}
      {(patientInfo || clientEntries.length > 0) && (
        <SectionCard 
          title="Demographics" 
          icon={User}
          subtitle={lastUpdated ? `Updated ${formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })}` : undefined}
          defaultOpen={true}
        >
          {patientInfo ? (
            <RenderDataFlat data={patientInfo} />
          ) : (
            <div className="space-y-1">
              {clientEntries.filter(([_, v]) => isSimpleValue(v)).map(([key, value], i) => (
                <DataRow 
                  key={key}
                  label={formatFieldName(key)}
                  value={renderValue(value)}
                  index={i}
                />
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* 2. ANAMNESIS DATA */}
      {anamnesisData && (
        <SectionCard title="Anamnesis Data" icon={Heart} defaultOpen={true}>
          {Array.isArray(anamnesisData) ? (
            <div className="space-y-6">
              {anamnesisData.map((anamnesis: Record<string, any>, index: number) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-3">
                    <SubHeading>
                      {anamnesis.questionnaire_type ? formatFieldName(anamnesis.questionnaire_type) : `Record ${index + 1}`}
                    </SubHeading>
                    {anamnesis.created_at && (
                      <span className="text-sm text-gray-600 font-medium">
                        {new Date(anamnesis.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {anamnesis.response_data && (
                    <RenderDataFlat data={anamnesis.response_data} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <RenderDataFlat data={anamnesisData} />
          )}
        </SectionCard>
      )}

      {/* 3. TREATMENT PROTOCOL */}
      {protocolData && (
        <SectionCard title="Treatment Protocol" icon={Stethoscope} defaultOpen={true}>
          <RenderDataFlat data={protocolData} />
        </SectionCard>
      )}

      {/* 4. ADDITIONAL DATA */}
      {collectedEntries.length > 0 && (
        <SectionCard title="Additional Data" icon={FileText} defaultOpen={true}>
          <div className="space-y-1">
            {collectedEntries.map(([key, value], i) => {
              if (isSimpleValue(value)) {
                return (
                  <DataRow 
                    key={key}
                    label={formatFieldName(key)}
                    value={renderValue(value)}
                    index={i}
                  />
                );
              }
              
              if (typeof value === 'object' && value !== null) {
                return (
                  <div key={key}>
                    <SectionHeading>{formatFieldName(key)}</SectionHeading>
                    <RenderDataFlat data={value} />
                  </div>
                );
              }
              
              return null;
            })}
          </div>
        </SectionCard>
      )}

      {/* 5. OTHER DB RECORDS */}
      {clientDbEntries.filter(([key]) => !['patient_info', 'anamnesis', 'protocol'].includes(key)).length > 0 && (
        <SectionCard title="Other Records" icon={Database} defaultOpen={true}>
          {clientDbEntries
            .filter(([key]) => !['patient_info', 'anamnesis', 'protocol'].includes(key))
            .map(([key, value], i) => {
              const label = clientData?.[`_${key}_label`] || key;
              
              if (value === null) {
                return (
                  <DataRow 
                    key={key}
                    label={formatFieldName(label)}
                    value={<span className="text-gray-400 italic">—</span>}
                    index={i}
                  />
                );
              }
              
              if (isSimpleValue(value)) {
                return (
                  <DataRow 
                    key={key}
                    label={formatFieldName(label)}
                    value={renderValue(value)}
                    index={i}
                  />
                );
              }
              
              return (
                <div key={key}>
                  <SectionHeading>{formatFieldName(label)}</SectionHeading>
                  {Array.isArray(value) ? (
                    <div className="space-y-4">
                      {value.map((item: any, j: number) => (
                        <div key={j} className="bg-surface rounded-xl p-4">
                          {typeof item === 'object' && item !== null ? (
                            <RenderDataFlat data={item} />
                          ) : (
                            renderValue(item)
                          )}
                        </div>
                      ))}
                    </div>
                  ) : typeof value === 'object' && value !== null ? (
                    <RenderDataFlat data={value} />
                  ) : (
                    renderValue(value)
                  )}
                </div>
              );
            })}
        </SectionCard>
      )}
    </div>
  );
}
